import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  useApproveLibraryItemMutation,
  useDeleteLibraryItemMutation,
  useGetLibraryDescendantsQuery,
} from '../../reactWrapper/Redux/apiSlice';
import { LIBRARY, describeError } from './library.config';

/** The list of everything underneath an item, shared by both dialogs. */
function DescendantList({ kind, id, onlyPending, open }) {
  const { data, isFetching } = useGetLibraryDescendantsQuery({ kind, id }, { skip: !open });
  if (isFetching || !data) return <p className='text-sm text-muted-foreground'>Checking what is underneath…</p>;
  const items = data.flat.filter((n) => !onlyPending || n.status === 'pending');
  if (!items.length) return <p className='text-sm text-muted-foreground'>Nothing {onlyPending ? 'pending ' : ''}underneath.</p>;
  return (
    <ul className='max-h-56 space-y-1 overflow-y-auto rounded-md border border-border bg-surface p-3 text-sm'>
      {items.map((n) => (
        <li key={`${n.kind}-${n.id}`} className='flex items-center gap-2'>
          <span className='w-16 shrink-0 font-mono text-[11px] uppercase tracking-wide text-muted-foreground'>{n.kind}</span>
          <span className='text-foreground'>{n.name}</span>
          {n.shared ? <span className='text-xs text-muted-foreground'>(also used elsewhere, will be unlinked only)</span> : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * The ⋯ menu at the end of every row: Edit, Approve (pending only), Delete.
 * Approve and Delete confirm in an AlertDialog that lists the descendants.
 */
export default function RowActions({ kind, item, onEdit }) {
  const [dialog, setDialog] = useState(null); // 'approve' | 'delete' | null
  const [approve, { isLoading: approving }] = useApproveLibraryItemMutation();
  const [remove, { isLoading: deleting }] = useDeleteLibraryItemMutation();
  const label = LIBRARY[kind].label.toLowerCase();
  const { data: desc } = useGetLibraryDescendantsQuery({ kind, id: item.id }, { skip: !dialog });

  const doApprove = async () => {
    try {
      const res = await approve({ kind, id: item.id }).unwrap();
      toast.success(`Approved "${item.name}"${res?.data?.approved > 1 ? ` and ${res.data.approved - 1} item${res.data.approved === 2 ? '' : 's'} under it` : ''}`);
      setDialog(null);
    } catch (err) {
      toast.error(describeError(err, kind));
    }
  };

  const doDelete = async () => {
    try {
      const res = await remove({ kind, id: item.id, parentId: item.parent_id || undefined }).unwrap();
      const d = res?.data || {};
      toast.success(`Deleted "${item.name}"${d.deleted > 1 ? ` and ${d.deleted - 1} more` : ''}${d.unlinked ? `, unlinked ${d.unlinked}` : ''}`);
      setDialog(null);
    } catch (err) {
      toast.error(describeError(err, kind));
    }
  };

  const pendingBelow = desc?.counts?.pending ?? 0;
  const totalBelow = desc?.counts?.total ?? 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            aria-label={`Actions for ${item.name}`}
            className='inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-surface hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
          >
            <MoreHorizontal className='h-4 w-4' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='min-w-[150px]'>
          <DropdownMenuItem onSelect={() => onEdit(item)}>Edit</DropdownMenuItem>
          <DropdownMenuItem disabled={item.status === 'approved'} onSelect={() => setDialog('approve')}>
            {item.status === 'approved' ? 'Approved' : 'Approve'}
          </DropdownMenuItem>
          <DropdownMenuItem className='text-red-600 focus:text-red-700' onSelect={() => setDialog('delete')}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={dialog === 'approve'} onOpenChange={(o) => !o && !approving && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {label} "{item.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Approving this {label} also approves the {pendingBelow} pending item{pendingBelow === 1 ? '' : 's'} underneath it. They become visible to companies posting jobs straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <DescendantList kind={kind} id={item.id} onlyPending open={dialog === 'approve'} />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={approving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doApprove(); }} disabled={approving} className='bg-brand-navy text-white hover:bg-brand-navy/90'>
              {approving ? 'Approving…' : `Approve ${pendingBelow + 1} item${pendingBelow ? 's' : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialog === 'delete'} onOpenChange={(o) => !o && !deleting && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label} "{item.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.{' '}
              {totalBelow
                ? `Everything connected to it goes too: ${totalBelow} item${totalBelow === 1 ? '' : 's'} listed below. Items also used elsewhere are only unlinked from this ${label}.`
                : 'Nothing else is connected to it.'}
              {kind === 'role' ? ' A role still referenced by an open job cannot be deleted.' : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {totalBelow ? <DescendantList kind={kind} id={item.id} open={dialog === 'delete'} /> : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className='bg-red-600 text-white hover:bg-red-700'>
              {deleting ? 'Deleting…' : `Delete ${totalBelow + 1} item${totalBelow ? 's' : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
